import { motion } from "framer-motion";
import { ArrowUpRight, Play } from "lucide-react";
import { Link } from "react-router-dom";
import { ProcurementMockup } from "@/components/mockups/DashboardMockups";
import { useEffect, useRef } from "react";

const heroLines = [
    ["Digital", "Solutions"],
    ["That", "Make", "Business"],
    ["Simpler."],
];

const easeOut = [0.16, 1, 0.3, 1];

export default function Hero() {
    const heroRef = useRef(null);
    useEffect(() => {
        const el = heroRef.current;
        if (!el) return;
        const onMove = (e) => {
            const rect = el.getBoundingClientRect();
            el.style.setProperty("--mx", `${e.clientX - rect.left}px`);
            el.style.setProperty("--my", `${e.clientY - rect.top}px`);
        };
        el.addEventListener("mousemove", onMove);
        return () => el.removeEventListener("mousemove", onMove);
    }, []);

    return (
        <section
            ref={heroRef}
            data-testid="hero-section"
            className="relative bg-[#0B1930] text-white overflow-hidden grain min-h-[100vh] pt-[72px]"
            style={{
                backgroundImage: "radial-gradient(1200px circle at var(--mx,50%) var(--my,20%), rgba(37,99,235,0.15), transparent 40%)",
            }}
        >
            {/* Ambient glows */}
            <div className="hero-glow top-[10%] left-[-10%]" style={{ background: "#2563EB" }} />
            <div className="hero-glow bottom-[-30%] right-[-10%]" style={{ background: "#E8B923" }} />

            {/* Vertical hairlines */}
            <div className="absolute inset-0 hidden md:block pointer-events-none">
                {[0, 25, 50, 75, 100].map((x) => (
                    <div key={x} className="absolute top-0 bottom-0 w-px bg-white/[0.04]" style={{ left: `${x}%` }} />
                ))}
            </div>

            <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-24 lg:pt-32 pb-24">
                {/* Eyebrow */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: easeOut }}
                    data-testid="hero-eyebrow"
                    className="flex items-center gap-3 mb-8"
                >
                    <div className="w-8 h-px bg-[#E8B923]" />
                    <span className="eyebrow">One Platform · Smarter Business</span>
                </motion.div>

                {/* Kinetic masked reveal */}
                <h1 data-testid="hero-headline" className="font-serif text-[13vw] sm:text-[10vw] lg:text-[8.5vw] leading-[0.92] tracking-tight font-medium">
                    {heroLines.map((line, li) => (
                        <span className="mask-line" key={li}>
                            <motion.span
                                initial={{ y: "110%", opacity: 0 }}
                                animate={{ y: "0%", opacity: 1 }}
                                transition={{ duration: 1, ease: easeOut, delay: 0.15 + li * 0.14 }}
                                className="inline-block"
                            >
                                {line.map((w, wi) => (
                                    <span key={wi} className={`inline-block mr-[0.15em] ${w === "Simpler." ? "italic text-[#E8B923]" : ""}`}>
                                        {w}
                                    </span>
                                ))}
                            </motion.span>
                        </span>
                    ))}
                </h1>

                <div className="grid lg:grid-cols-12 gap-8 mt-12">
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: easeOut, delay: 0.7 }}
                        className="lg:col-span-6 text-white/70 text-lg leading-relaxed"
                    >
                        Enterprise software designed to automate processes, improve visibility and help organizations —
                        from ministries to enterprises — operate smarter across procurement, contracts, property,
                        events, IT operations and document authentication.
                    </motion.p>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: easeOut, delay: 0.85 }}
                        className="lg:col-span-5 lg:col-start-8 flex flex-col sm:flex-row lg:flex-col gap-3 lg:justify-end"
                    >
                        <Link to="/products" data-testid="hero-cta-primary" className="btn-gold">
                            Explore Solutions <ArrowUpRight className="w-4 h-4" />
                        </Link>
                        <Link to="/request-demo" data-testid="hero-cta-secondary" className="btn-ghost-navy">
                            <Play className="w-3.5 h-3.5" /> Request a Demo
                        </Link>
                    </motion.div>
                </div>

                {/* Featured mockup with parallax tilt */}
                <motion.div
                    initial={{ opacity: 0, y: 60 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1.2, ease: easeOut, delay: 1 }}
                    className="mt-20 grid lg:grid-cols-12 gap-6 items-end"
                >
                    <div className="lg:col-span-3 space-y-6">
                        {[
                            { k: "5", l: "Core products" },
                            { k: "12+", l: "Industries served" },
                            { k: "99.9%", l: "Uptime SLA" },
                        ].map((s) => (
                            <div key={s.l} className="pb-4 border-b border-white/10">
                                <div className="font-serif text-3xl text-[#E8B923]">{s.k}</div>
                                <div className="text-white/50 text-xs font-mono tracking-widest mt-2 uppercase">{s.l}</div>
                            </div>
                        ))}
                    </div>
                    <div className="lg:col-span-9">
                        <TiltMockup />
                    </div>
                </motion.div>
            </div>
        </section>
    );
}

function TiltMockup() {
    const ref = useRef(null);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const onMove = (e) => {
            const r = el.getBoundingClientRect();
            const x = (e.clientX - r.left) / r.width - 0.5;
            const y = (e.clientY - r.top) / r.height - 0.5;
            el.style.transform = `perspective(1200px) rotateX(${-y * 4}deg) rotateY(${x * 4}deg)`;
        };
        const onLeave = () => { el.style.transform = "perspective(1200px) rotateX(0deg) rotateY(0deg)"; };
        el.addEventListener("mousemove", onMove);
        el.addEventListener("mouseleave", onLeave);
        return () => { el.removeEventListener("mousemove", onMove); el.removeEventListener("mouseleave", onLeave); };
    }, []);
    return (
        <div ref={ref} className="transition-transform duration-300 ease-out">
            <ProcurementMockup />
        </div>
    );
}
